import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { FileText, CreditCard, User, LogOut, CheckCircle, Clock, XCircle, Key, Download, Wifi, DollarSign, Calendar, Printer, Loader2, LifeBuoy, Plus, MapPin, Phone, Mail, Globe, ChevronDown, MessageCircle, Send, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import CustomerWiFiManagement from '../components/CustomerWiFiManagement';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Subdomain to Tenant mapping for custom portal domains
const PORTAL_DOMAIN_MAPPING = {
  'portal.radiuslink.id': 'T06122503',
};

// Get tenant code from subdomain or URL params
const getTenantCode = (urlTenantSlug) => {
  const hostname = window.location.hostname;
  return PORTAL_DOMAIN_MAPPING[hostname] || urlTenantSlug;
};

const TenantCustomerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantSlug: urlTenantSlug } = useParams();
  const tenantSlug = getTenantCode(urlTenantSlug);
  const [customer, setCustomer] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [autoPayHandled, setAutoPayHandled] = useState(false);
  const [urlSearchParams] = useSearchParams();
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard' | 'akun' | 'tagihan' | 'internet' | 'help'
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketForm, setTicketForm] = useState({ title: '', category: 'general', priority: 'medium', description: '' });
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [showContactInfo, setShowContactInfo] = useState(true);
  const [historyFilterMonth, setHistoryFilterMonth] = useState('all');
  const [historyFilterYear, setHistoryFilterYear] = useState('all');

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 3;
    
    const checkAuth = () => {
      const token = localStorage.getItem('customer_token');
      const customerData = localStorage.getItem('customer_data');
      const storedSlug = localStorage.getItem('tenant_slug');

      if (!token || !customerData) {
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(checkAuth, 100);
          return;
        }
        navigate(`/p/${tenantSlug}/login`);
        return;
      }

      // Verify tenant slug matches
      if (storedSlug && storedSlug !== tenantSlug) {
        navigate(`/p/${storedSlug}/dashboard`);
        return;
      }

      const parsedCustomer = JSON.parse(customerData);
      setCustomer(parsedCustomer);
      fetchCustomerStatus(token, parsedCustomer.id);
      fetchTenantInfo();
      fetchInvoices(token);
      
      // Auto-refresh invoices every 60 minutes to catch payment updates
      const refreshInterval = setInterval(() => {
        fetchInvoices(token);
      }, 3600000);
      
      return () => clearInterval(refreshInterval);
    };
    
    checkAuth();
  }, [navigate, tenantSlug, location.key]); // Re-run when navigating back to this page

  useEffect(() => {
    if (activeTab === 'help') fetchMyTickets();
  }, [activeTab]);

  const fetchCustomerStatus = async (token, customerId) => {
    try {
      const response = await axios.get(`${API_URL}/api/v2/customer/status?customer_id=${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.data) {
        setCustomer(prev => ({
          ...prev,
          is_online: response.data.data.is_online,
          wan_device_id: response.data.data.wan_device_id
        }));
        // Update localStorage
        const updatedCustomer = { ...JSON.parse(localStorage.getItem("customer_data")), ...response.data.data };
        localStorage.setItem("customer_data", JSON.stringify(updatedCustomer));
      }
    } catch (error) {
      console.log("Could not fetch customer status:", error.message);
    }
  };

  const fetchTenantInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/portal/${tenantSlug}/info`);
      if (response.data.success) {
        setTenantInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching tenant info:', error);
    }
  };

  const fetchInvoices = async (token) => {
    try {
      const [invoicesRes, statsRes] = await Promise.all([
        axios.get(
          `${API_URL}/api/v2/customer/invoices`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${API_URL}/api/v2/customer/dashboard-stats`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ]);

      if (invoicesRes.data.success) {
        setInvoices(invoicesRes.data.data);
      }
      
      if (statsRes.data.success) {
        setDashboardStats(statsRes.data.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        toast.error('Sesi Anda telah berakhir. Silakan login kembali.');
        handleLogout();
      } else {
        toast.error('Gagal memuat data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-open payment modal when arrived from QR invoice link (?invoice=INV-XXX)
  useEffect(() => {
    if (autoPayHandled) return;
    const targetInv = urlSearchParams.get('invoice');
    if (!targetInv || !invoices || invoices.length === 0) return;
    const match = invoices.find(inv => inv.invoice_number === targetInv && !inv.is_paid);
    if (match) {
      setAutoPayHandled(true);
      setTimeout(() => handlePayment(match), 300);
    } else {
      setAutoPayHandled(true);
    }
  }, [invoices, urlSearchParams, autoPayHandled]);

  const handlePayment = async (invoice) => {
    // Redirect to payment confirmation page with select mode to choose payment method
    window.location.href = `/payment/confirmation?invoice_id=${invoice.id}&select=true`;
  };

  const [syncingInvoice, setSyncingInvoice] = useState(null);
  
  const handleSyncPayment = async (invoice) => {
    const token = localStorage.getItem('customer_token');
    setSyncingInvoice(invoice.id);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/v2/customer/invoice/${invoice.id}/sync-payment`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const result = response.data.data;
        if (result.synced) {
          toast.success('✅ Pembayaran berhasil diverifikasi! Status: LUNAS');
          // Refresh invoices
          fetchInvoices(token);
        } else if (result.status === 'LUNAS') {
          toast.success('Invoice sudah lunas');
        } else {
          toast.info(`Status pembayaran: ${result.status}`);
        }
      } else {
        toast.error(response.data.data?.message || 'Gagal cek status');
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Gagal mengecek status pembayaran');
    } finally {
      setSyncingInvoice(null);
    }
  };

  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    const token = localStorage.getItem('customer_token');
    setDownloadingInvoice(invoiceId);

    try {
      const response = await axios.get(
        `${API_URL}/api/v2/customer/invoice/${invoiceId}/print-data`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const { invoice, customer: customerData, company_settings } = response.data.data;
        
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          toast.error('Mohon izinkan popup untuk download invoice');
          return;
        }

        const printHTML = generatePrintHTML(invoice, customerData, company_settings);
        printWindow.document.write(printHTML);
        printWindow.document.close();
        
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 250);
        };

        toast.success('Invoice siap di-download');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Gagal download invoice');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const generatePrintHTML = (invoice, customerData, companySettings) => {
    const formatDate = (dateStr) => {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' });
    };
    const formatCurrency = (amount) => new Intl.NumberFormat('id-ID').format(Number(amount) || 0);

    const isPaid = invoice.is_paid || String(invoice.status).toUpperCase() === 'PAID' || String(invoice.status).toUpperCase() === 'LUNAS';
    const logoUrl = companySettings?.logo_invoice_url || companySettings?.logo_url || tenantInfo?.logo_url || tenantInfo?.branding?.logo_url || '';
    const companyName = companySettings?.company_name || tenantInfo?.tenant_name || 'ISP Provider';
    const companyAddress = companySettings?.company_address || tenantInfo?.address || '';
    const companyPhone = companySettings?.company_phone || tenantInfo?.phone || '';
    const companyEmail = companySettings?.company_email || tenantInfo?.email || '';

    const custName = invoice.customer_name || customerData?.name || '-';
    const custAcc = invoice.customer_account_number || customerData?.account_number || '-';
    const custAddr = invoice.customer_address || customerData?.address || '';
    const custPhone = invoice.customer_phone || customerData?.phone || '';

    const serviceName = invoice.service_name || invoice.package_name || invoice.service_plan || customerData?.service_plan || 'Layanan Internet';
    const _mNames = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
    let _pm = invoice.period_month;
    let _py = invoice.period_year;
    if (!_pm || !_py) {
      const _mm = String(invoice.invoice_number || '').match(/INV-(\d{4})(\d{2})/);
      if (_mm) { _py = parseInt(_mm[1], 10); _pm = parseInt(_mm[2], 10); }
    }
    const period = (_pm && _py) ? `${_mNames[parseInt(_pm, 10) - 1]} ${_py}` : '-';

    const subtotalVal = invoice.subtotal || invoice.base_amount || invoice.total;
    const taxPct = invoice.tax_percentage || 0;
    const taxAmt = invoice.tax_amount || 0;
    const totalVal = invoice.total_amount || invoice.total;
    const bankAccounts = Array.isArray(companySettings?.bank_accounts) ? companySettings.bank_accounts : [];

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Invoice ${invoice.invoice_number}</title>
  <style>
    @page { size: A4; margin: 12mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #111827; background: #fff; padding: 10mm; }
    .invoice { max-width: 190mm; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }
    .header-left img { max-height: 60px; max-width: 180px; margin-bottom: 6px; object-fit: contain; }
    .header-left .cname { font-weight: bold; font-size: 13px; color: #111827; }
    .header-left .cdet { font-size: 10px; color: #4b5563; line-height: 1.5; }
    .header-right { text-align: right; }
    .header-right h1 { font-size: 30px; color: #312e81; font-weight: 800; margin-bottom: 4px; letter-spacing: 1px; }
    .header-right .inum { font-size: 11px; color: #6b7280; margin-bottom: 8px; }
    .badge { display: inline-block; padding: 6px 16px; font-size: 12px; font-weight: bold; border-radius: 4px; color: #fff; }
    .badge-paid { background: #10b981; }
    .badge-unpaid { background: #3b82f6; }
    .divider { border-bottom: 2px solid #4f46e5; margin-bottom: 16px; }
    .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 20px; }
    .section-title { font-size: 11px; font-weight: bold; color: #312e81; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
    .info p { font-size: 11px; margin: 2px 0; }
    .info .lbl { color: #6b7280; }
    .info .val { font-weight: 600; color: #111827; }
    .kv { display: flex; justify-content: space-between; padding: 2px 0; }
    .items { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 11px; }
    .items thead th { background: #f3f4f6; border-bottom: 2px solid #4f46e5; padding: 8px 10px; text-align: left; font-weight: bold; color: #111827; }
    .items thead th.right { text-align: right; }
    .items tbody td { padding: 8px 10px; border-bottom: 1px solid #e5e7eb; }
    .items tbody td.right { text-align: right; font-weight: bold; }
    .totals-wrap { display: flex; justify-content: flex-end; margin-bottom: 20px; }
    .totals { width: 280px; background: #f9fafb; border: 1px solid #d1d5db; border-radius: 6px; padding: 14px; }
    .totals .row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 11px; }
    .totals .row.total { border-top: 2px solid #4f46e5; margin-top: 6px; padding-top: 8px; font-size: 15px; font-weight: bold; color: #312e81; }
    .payinfo { margin-bottom: 20px; }
    .payinfo table { width: 100%; border-collapse: collapse; font-size: 11px; }
    .payinfo th, .payinfo td { border: 1px solid #d1d5db; padding: 8px 10px; }
    .payinfo thead th { background: #f3f4f6; font-weight: bold; text-align: left; }
    .payinfo td.right { text-align: right; font-weight: bold; }
    .payinfo .note { font-size: 10px; color: #6b7280; font-style: italic; margin-top: 6px; }
    .bank { margin-bottom: 16px; font-size: 11px; }
    .bank h4 { font-size: 12px; color: #312e81; font-weight: bold; margin-bottom: 6px; }
    .bank ul { list-style: disc; padding-left: 20px; }
    .bank li { margin: 3px 0; }
    .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #d1d5db; text-align: center; }
    .footer .tks { font-size: 13px; font-weight: bold; color: #312e81; margin-bottom: 4px; }
    .footer .digi { font-size: 9px; color: #6b7280; font-style: italic; }
    @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="invoice">
    <div class="header">
      <div class="header-left">
        ${logoUrl ? `<img src="${logoUrl}" alt="Logo">` : ''}
        <div class="cname">${companyName}</div>
        ${companyAddress ? `<div class="cdet">${companyAddress}</div>` : ''}
        ${companyPhone ? `<div class="cdet">Telp: ${companyPhone}</div>` : ''}
        ${companyEmail ? `<div class="cdet">Email: ${companyEmail}</div>` : ''}
      </div>
      <div class="header-right">
        <h1>INVOICE</h1>
        <div class="inum">${invoice.invoice_number}</div>
        <span class="badge ${isPaid ? 'badge-paid' : 'badge-unpaid'}">${isPaid ? 'LUNAS' : (String(invoice.status || 'BELUM BAYAR').toUpperCase())}</span>
      </div>
    </div>

    <div class="divider"></div>

    <div class="grid2">
      <div class="info">
        <div class="section-title">Ditagihkan Ke:</div>
        <p class="lbl">ID: <span class="val">${custAcc}</span></p>
        <p class="val" style="font-size:13px; margin:4px 0;">${custName}</p>
        ${custAddr ? `<p class="val" style="font-size:10px; line-height:1.5;">${custAddr}</p>` : ''}
        ${custPhone ? `<p class="lbl">HP: <span class="val">${custPhone}</span></p>` : ''}
      </div>
      <div class="info">
        <div class="section-title">Info Invoice:</div>
        <div class="kv"><span class="lbl">Tanggal:</span><span class="val">${formatDate(invoice.issue_date || invoice.created_at)}</span></div>
        <div class="kv"><span class="lbl">Jatuh Tempo:</span><span class="val">${formatDate(invoice.display_due_date || invoice.due_date)}</span></div>
        ${invoice.paid_date ? `<div class="kv"><span class="lbl">Tgl Bayar:</span><span class="val">${formatDate(invoice.paid_date)}</span></div>` : ''}
        <div class="kv"><span class="lbl">Periode:</span><span class="val">${period}</span></div>
      </div>
    </div>

    <div class="section-title" style="margin-bottom:8px;">Ringkasan Layanan</div>
    <table class="items">
      <thead>
        <tr>
          <th>Paket</th>
          <th>Jatuh Tempo</th>
          <th>Periode</th>
          <th class="right">Jumlah</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>${serviceName}</td>
          <td>${formatDate(invoice.display_due_date || invoice.due_date)}</td>
          <td>${period}</td>
          <td class="right">Rp ${formatCurrency(totalVal)}</td>
        </tr>
      </tbody>
    </table>

    <div class="totals-wrap">
      <div class="totals">
        <div class="row"><span>Subtotal:</span><span>Rp ${formatCurrency(subtotalVal)}</span></div>
        <div class="row"><span>PPN (${taxPct}%):</span><span>Rp ${formatCurrency(taxAmt)}</span></div>
        <div class="row total"><span>Total:</span><span>Rp ${formatCurrency(totalVal)}</span></div>
      </div>
    </div>

    ${isPaid ? `
    <div class="payinfo">
      <table>
        <thead>
          <tr>
            <th>Tanggal Bayar</th>
            <th>Metode</th>
            <th>No. Invoice</th>
            <th class="right">Jumlah</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>${formatDate(invoice.paid_date)}</td>
            <td>${invoice.payment_method || invoice.payment_channel || 'Tunai / Manual'}</td>
            <td>${invoice.invoice_number}</td>
            <td class="right">Rp ${formatCurrency(totalVal)}</td>
          </tr>
        </tbody>
      </table>
      <p class="note">Pembayaran telah diterima. Terima kasih atas kepercayaan Anda.</p>
    </div>
    ` : (bankAccounts.length > 0 ? `
    <div class="bank">
      <h4>Transfer Manual</h4>
      <ul>
        ${bankAccounts.map(b => `<li><strong>${b.bank_name || ''}</strong> &bull; ${b.account_name || ''} &bull; <code>${b.account_number || ''}</code></li>`).join('')}
      </ul>
    </div>
    ` : '')}

    <div class="footer">
      <div class="tks">Terima Kasih Atas Kepercayaan Anda</div>
      <div class="digi">Ini adalah invoice digital yang sah dan tidak memerlukan tanda tangan.</div>
    </div>
  </div>

  <script>
    window.onload = function() { setTimeout(function() { window.print(); }, 250); }
  </script>
</body>
</html>
    `;
  };

  const handleLogout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_data');
    localStorage.removeItem('tenant_slug');
    navigate(`/p/${tenantSlug}/login`);
  };

  const fetchMyTickets = async () => {
    try {
      const token = localStorage.getItem('customer_token');
      if (!token) return;
      const res = await axios.get(`${API_URL}/api/v2/portal/${tenantSlug}/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success) setTickets(res.data.data || []);
    } catch (err) {
      console.error('fetchMyTickets error:', err);
    }
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!ticketForm.title.trim() || !ticketForm.description.trim()) {
      toast.error('Judul dan deskripsi wajib diisi');
      return;
    }
    setTicketSubmitting(true);
    try {
      const token = localStorage.getItem('customer_token');
      const res = await axios.post(
        `${API_URL}/api/v2/portal/${tenantSlug}/tickets`,
        {
          title: ticketForm.title.trim(),
          description: ticketForm.description.trim(),
          category: ticketForm.category,
          priority: ticketForm.priority
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        toast.success(`Tiket ${res.data.data.ticket_number} berhasil dibuat`);
        setShowTicketModal(false);
        setTicketForm({ title: '', category: 'general', priority: 'medium', description: '' });
        fetchMyTickets();
      } else {
        toast.error(res.data?.detail || 'Gagal membuat tiket');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal membuat tiket');
    } finally {
      setTicketSubmitting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Password baru tidak cocok');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      const token = localStorage.getItem('customer_token');
      await axios.post(
        `${API_URL}/api/v2/customer/change-password`,
        null,
        {
          params: {
            current_password: passwordForm.currentPassword,
            new_password: passwordForm.newPassword
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Password berhasil diubah!');
      setShowChangePassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengubah password');
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center dark:bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
      </div>
    );
  }

  const unpaidInvoices = invoices.filter(inv => !inv.is_paid);
  const paidInvoices = invoices.filter(inv => inv.is_paid);

  // Filter helpers for Riwayat Pembayaran (Bulan/Tahun)
  const _getInvDate = (inv) => inv.issue_date || inv.paid_date || inv.created_at;
  const historyYears = Array.from(new Set(
    paidInvoices.map(inv => {
      const d = _getInvDate(inv);
      return d ? new Date(d).getFullYear() : null;
    }).filter(Boolean)
  )).sort((a, b) => b - a);
  const filteredPaidInvoices = paidInvoices.filter(inv => {
    const d = _getInvDate(inv);
    if (!d) return historyFilterMonth === 'all' && historyFilterYear === 'all';
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = dt.getMonth() + 1;
    if (historyFilterYear !== 'all' && y !== parseInt(historyFilterYear, 10)) return false;
    if (historyFilterMonth !== 'all' && m !== parseInt(historyFilterMonth, 10)) return false;
    return true;
  });
  const _monthNames = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
  const _formatPeriod = (inv) => {
    // Prefer explicit period_month/period_year from DB; fallback: parse from INV-YYYYMM
    let pm = inv?.period_month;
    let py = inv?.period_year;
    if (!pm || !py) {
      const m = String(inv?.invoice_number || '').match(/INV-(\d{4})(\d{2})/);
      if (m) { py = parseInt(m[1], 10); pm = parseInt(m[2], 10); }
    }
    if (!pm || !py) return '-';
    const idx = parseInt(pm, 10) - 1;
    if (idx < 0 || idx > 11) return '-';
    return `${_monthNames[idx]} ${py}`;
  };
  const primaryColor = tenantInfo?.branding?.primary_color || '#3b82f6';

  return (
    <div className="min-h-screen" style={{ background: '#F5F7FA' }}>
      {/* Modern Glassmorphism Header */}
      <header className="backdrop-blur-xl bg-white/90 border-b border-neutral-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              {(tenantInfo?.branding?.logo_portal_url || tenantInfo?.branding?.logo_url) ? (
                <img 
                  src={tenantInfo.branding.logo_portal_url || tenantInfo.branding.logo_url} 
                  alt={tenantInfo.tenant_name}
                  className="h-11 sm:h-14 w-auto object-contain"
                  style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.08)) contrast(1.05) saturate(1.1)' }}
                  data-testid="portal-logo"
                />
              ) : (
                <div 
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md"
                  style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}
                >
                  {tenantInfo?.tenant_name?.charAt(0) || 'P'}
                </div>
              )}
            </div>

            {/* Top Navigation Menu — Desktop */}
            <nav className="hidden sm:flex items-center gap-1" data-testid="top-nav">
              {[
                { id: 'dashboard', label: 'Dashboard', Icon: Home },
                { id: 'akun', label: 'Akun', Icon: User },
                { id: 'tagihan', label: 'Tagihan', Icon: CreditCard, badge: unpaidInvoices.length },
                { id: 'internet', label: 'Internet', Icon: Wifi, disabled: !customer?.wan_device_id },
                { id: 'help', label: 'Bantuan', Icon: LifeBuoy, badge: tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length },
              ].map(({ id, label, Icon, disabled, badge }) => (
                <button
                  key={id}
                  onClick={() => !disabled && setActiveTab(id)}
                  data-testid={`nav-${id}`}
                  disabled={disabled}
                  className={`relative flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                    activeTab === id
                      ? ''
                      : 'text-neutral-500 hover:text-neutral-800 hover:bg-neutral-50'
                  } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                  style={activeTab === id ? {
                    background: `${primaryColor}1a`,
                    color: primaryColor,
                  } : {}}
                >
                  <Icon className="w-4 h-4" strokeWidth={2.4} />
                  <span className="hidden md:inline">{label}</span>
                  {badge > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm" data-testid={`nav-${id}-badge`}>
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>

            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-neutral-200 text-neutral-600 hover:bg-neutral-50 hover:border-neutral-300 h-9 px-3 sm:px-4 text-sm font-medium transition-all"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Keluar</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Tab Navigation — grid 5 cols, icon on top + label below */}
      <nav className="sm:hidden sticky top-[56px] z-40 bg-white border-b border-neutral-200 shadow-sm dark:bg-slate-800" data-testid="mobile-nav">
        <div className="grid grid-cols-5 gap-0.5 px-1 py-1.5">
          {[
            { id: 'dashboard', label: 'Dashboard', Icon: Home },
            { id: 'akun', label: 'Akun', Icon: User },
            { id: 'tagihan', label: 'Tagihan', Icon: CreditCard, badge: unpaidInvoices.length },
            { id: 'internet', label: 'Internet', Icon: Wifi, disabled: !customer?.wan_device_id },
            { id: 'help', label: 'Bantuan', Icon: LifeBuoy, badge: tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length },
          ].map(({ id, label, Icon, disabled, badge }) => (
            <button
              key={id}
              onClick={() => !disabled && setActiveTab(id)}
              data-testid={`mnav-${id}`}
              disabled={disabled}
              className={`relative flex flex-col items-center justify-center gap-0.5 py-1.5 px-1 rounded-lg text-[10px] font-semibold transition-all ${
                activeTab === id ? '' : 'text-neutral-500'
              } ${disabled ? 'opacity-40' : ''}`}
              style={activeTab === id ? { background: `${primaryColor}1a`, color: primaryColor } : {}}
            >
              <Icon className="w-5 h-5" strokeWidth={2.2} />
              <span className="leading-none">{label}</span>
              {badge > 0 && (
                <span className="absolute top-0.5 right-1.5 min-w-[15px] h-[15px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center shadow" data-testid={`mnav-${id}-badge`}>
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {activeTab === 'dashboard' && (
          <>
        {/* Welcome Banner — Compact Professional */}
        <div
          className="relative overflow-hidden rounded-xl mb-4 px-4 py-3 sm:px-5 sm:py-3.5 flex items-center justify-between gap-3"
          style={{
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 55%, ${primaryColor}aa 100%)`
          }}
          data-testid="welcome-banner"
        >
          <div className="absolute inset-0 opacity-[0.07] pointer-events-none">
            <div className="absolute top-0 right-0 w-24 h-24 bg-white rounded-full -mr-10 -mt-10 dark:bg-slate-800"></div>
          </div>
          <div className="relative z-10 min-w-0 flex-1">
            <p className="text-white/75 text-[11px] sm:text-xs font-medium leading-tight">Selamat datang kembali,</p>
            <h2 className="text-white text-base sm:text-lg font-bold truncate leading-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
              {customer?.name || 'Pelanggan'}
            </h2>
          </div>
          <div className="relative z-10 hidden sm:flex flex-col items-end text-right shrink-0">
            <span className="text-white/80 text-[10px] uppercase tracking-wider">ID Pelanggan</span>
            <span className="text-white text-sm font-mono font-semibold">{customer?.account_number || '-'}</span>
          </div>
        </div>

        {/* Premium Stats Cards */}
        {dashboardStats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6" data-testid="stats-cards">
            {/* Tagihan Belum Dibayar */}
            <div className="group bg-white rounded-xl border border-neutral-200 p-3 sm:p-4 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 dark:bg-slate-800" data-testid="unpaid-card">
              <div className="flex items-center justify-between mb-2">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-200">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${dashboardStats.payment_status_computed === 'LUNAS' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                  {dashboardStats.payment_status_computed === 'LUNAS' ? 'LUNAS' : 'BELUM LUNAS'}
                </span>
              </div>
              <p className="text-[11px] text-neutral-500 font-medium uppercase tracking-wide mb-1">Tagihan</p>
              <p className="text-xl sm:text-2xl font-bold text-neutral-800" style={{fontFamily: 'Outfit, sans-serif'}}>{dashboardStats.unpaid_count}</p>
              <p className="text-xs sm:text-sm font-semibold text-orange-500 mt-1">
                Rp {dashboardStats.total_unpaid?.toLocaleString('id-ID')}
              </p>
            </div>

            {/* Jatuh Tempo */}
            <div className="group bg-white rounded-xl border border-neutral-200 p-3 sm:p-4 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 dark:bg-slate-800" data-testid="due-date-card">
              <div className="flex items-center justify-between mb-2">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
              </div>
              <p className="text-[11px] text-neutral-500 font-medium uppercase tracking-wide mb-1">Jatuh Tempo</p>
              <p className="text-xl sm:text-2xl font-bold text-neutral-800" style={{fontFamily: 'Outfit, sans-serif'}}>
                {dashboardStats.next_due_date 
                  ? new Date(dashboardStats.next_due_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short' })
                  : '-'
                }
              </p>
              <p className="text-xs text-neutral-400 mt-1">
                {dashboardStats.next_due_date 
                  ? new Date(dashboardStats.next_due_date).toLocaleDateString('id-ID', { year: 'numeric' })
                  : ''
                }
              </p>
            </div>

            {/* Paket Layanan */}
            <div className="group bg-white rounded-xl border border-neutral-200 p-3 sm:p-4 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 dark:bg-slate-800" data-testid="plan-card">
              <div className="flex items-center justify-between mb-2">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-200">
                  <Wifi className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
              </div>
              <p className="text-[11px] text-neutral-500 font-medium uppercase tracking-wide mb-1">Paket Aktif</p>
              <p className="text-sm sm:text-base font-bold text-neutral-800 truncate" style={{fontFamily: 'Outfit, sans-serif'}}>{dashboardStats.service_plan || '-'}</p>
              <p className="text-xs sm:text-sm font-semibold text-emerald-500 mt-1">
                Rp {dashboardStats.monthly_fee?.toLocaleString('id-ID')}<span className="text-xs text-neutral-400">/bln</span>
              </p>
            </div>

            {/* Sisa Waktu dengan Progress Bar */}
            <div className="group bg-white rounded-xl border border-neutral-200 p-3 sm:p-4 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 dark:bg-slate-800" data-testid="remaining-card">
              <div className="flex items-center justify-between mb-2">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-violet-400 to-violet-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-200">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
              </div>
              <p className="text-[11px] text-neutral-500 font-medium uppercase tracking-wide mb-1">Sisa Waktu</p>
              <p className="text-xl sm:text-2xl font-bold text-neutral-800" style={{fontFamily: 'Outfit, sans-serif'}}>
                {dashboardStats.days_remaining || 0}
                <span className="text-xs font-normal text-neutral-400 ml-1">hari</span>
              </p>
              <div className="mt-2 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-violet-400 to-violet-600 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(100, ((dashboardStats.days_remaining || 0) / 30) * 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}

          </>
        )}

        {/* Akun Tab — Customer Info */}
        {activeTab === 'akun' && (
        <div className="bg-white rounded-xl border border-neutral-200 p-5 sm:p-6 mb-6 dark:bg-slate-800" data-testid="customer-info">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md"
                style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)` }}
              >
                {customer?.name?.charAt(0) || 'P'}
              </div>
              <div>
                <h2 className="text-lg font-bold text-neutral-800" style={{fontFamily: 'Outfit, sans-serif'}}>Informasi Akun</h2>
                <p className="text-xs text-neutral-500">Detail pelanggan dan status layanan</p>
              </div>
            </div>
            <Button
              onClick={() => setShowChangePassword(true)}
              variant="outline"
              size="sm"
              className="h-9 px-4 text-sm font-medium border-neutral-200 hover:border-neutral-300 transition-all"
              data-testid="change-password-btn"
            >
              <Key className="w-4 h-4 mr-2" />
              Ubah Password
            </Button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            <div className="p-3 bg-neutral-50 rounded-lg">
              <p className="text-[10px] sm:text-xs text-neutral-500 font-medium uppercase tracking-wide mb-1">ID Pelanggan</p>
              <p className="text-sm sm:text-base font-semibold text-neutral-800 break-all">{customer?.account_number}</p>
            </div>
            <div className="p-3 bg-neutral-50 rounded-lg">
              <p className="text-[10px] sm:text-xs text-neutral-500 font-medium uppercase tracking-wide mb-1">Nama</p>
              <p className="text-sm sm:text-base font-semibold text-neutral-800">{customer?.name}</p>
            </div>
            <div className="p-3 bg-neutral-50 rounded-lg">
              <p className="text-[10px] sm:text-xs text-neutral-500 font-medium uppercase tracking-wide mb-1">Status Bayar</p>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                (dashboardStats?.payment_status_computed === 'LUNAS' || customer?.payment_status_computed === 'LUNAS' || unpaidInvoices.length === 0)
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`} data-testid="payment-status">
                {(dashboardStats?.payment_status_computed === 'LUNAS' || customer?.payment_status_computed === 'LUNAS' || unpaidInvoices.length === 0) 
                  ? <><CheckCircle className="w-3.5 h-3.5" /> LUNAS</> 
                  : <><XCircle className="w-3.5 h-3.5" /> BELUM LUNAS</>}
              </span>
            </div>
            <div className="p-3 bg-neutral-50 rounded-lg">
              <p className="text-[10px] sm:text-xs text-neutral-500 font-medium uppercase tracking-wide mb-1">Koneksi</p>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                customer?.is_online
                  ? 'bg-emerald-100 text-emerald-700' 
                  : 'bg-neutral-200 text-neutral-600'
              }`} data-testid="connection-status">
                <span className={`w-2 h-2 rounded-full ${customer?.is_online ? 'bg-emerald-500 animate-pulse' : 'bg-neutral-400'}`}></span>
                {customer?.is_online ? 'ONLINE' : 'OFFLINE'}
              </span>
            </div>
          </div>

          {/* Detail Lengkap Akun */}
          <div className="mt-5 pt-5 border-t border-neutral-200">
            <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3">Detail Akun</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Phone */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-neutral-50" data-testid="akun-phone">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${primaryColor}1a`, color: primaryColor }}>
                  <Phone className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-neutral-500 uppercase tracking-wide font-medium">Telepon</p>
                  <p className="text-sm font-semibold text-neutral-800 truncate">{customer?.phone || '-'}</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-neutral-50" data-testid="akun-email">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${primaryColor}1a`, color: primaryColor }}>
                  <Mail className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-neutral-500 uppercase tracking-wide font-medium">Email</p>
                  <p className="text-sm font-semibold text-neutral-800 truncate">{customer?.email || '-'}</p>
                </div>
              </div>

              {/* Address - full width */}
              <div className="flex items-start gap-3 p-3 rounded-lg bg-neutral-50 sm:col-span-2" data-testid="akun-address">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${primaryColor}1a`, color: primaryColor }}>
                  <MapPin className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-neutral-500 uppercase tracking-wide font-medium">Alamat</p>
                  <p className="text-sm font-semibold text-neutral-800 leading-relaxed">{customer?.address || '-'}</p>
                </div>
              </div>

              {/* Service Package */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-neutral-50" data-testid="akun-service">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${primaryColor}1a`, color: primaryColor }}>
                  <Wifi className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-neutral-500 uppercase tracking-wide font-medium">Paket Layanan</p>
                  <p className="text-sm font-semibold text-neutral-800 truncate">{customer?.service_plan || customer?.service_plan_name || '-'}</p>
                </div>
              </div>

              {/* Registration Date + Lama Berlangganan badge */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-neutral-50" data-testid="akun-regdate">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${primaryColor}1a`, color: primaryColor }}>
                  <Calendar className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-neutral-500 uppercase tracking-wide font-medium">Tanggal Registrasi</p>
                  <p className="text-sm font-semibold text-neutral-800">
                    {(() => {
                      const r = customer?.registration_date;
                      if (!r) return '-';
                      const [y, m, d] = String(r).slice(0,10).split('-');
                      if (!y || !m || !d) return r;
                      const months = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
                      return `${parseInt(d,10)} ${months[parseInt(m,10)-1]} ${y}`;
                    })()}
                  </p>
                  {(() => {
                    const r = customer?.registration_date;
                    if (!r) return null;
                    const [y, m, d] = String(r).slice(0,10).split('-').map(n => parseInt(n,10));
                    if (!y || !m || !d) return null;
                    const regDate = new Date(y, m-1, d);
                    const now = new Date();
                    const months = (now.getFullYear()-regDate.getFullYear())*12 + (now.getMonth()-regDate.getMonth()) - (now.getDate() < regDate.getDate() ? 1 : 0);
                    if (months < 0) return null;
                    const years = Math.floor(months/12);
                    const remMonths = months % 12;
                    let label, bg, color, emoji;
                    if (months < 6) { label = `Pelanggan Baru • ${months || 1} bulan`; bg='#dbeafe'; color='#1e40af'; emoji='🌟'; }
                    else if (months < 12) { label = `Pelanggan Aktif • ${months} bulan`; bg='#d1fae5'; color='#065f46'; emoji='✨'; }
                    else if (years < 3) { label = `Pelanggan Setia • ${years} thn${remMonths ? ` ${remMonths} bln` : ''}`; bg='#fef3c7'; color='#92400e'; emoji='🏆'; }
                    else { label = `Pelanggan VIP • ${years} thn${remMonths ? ` ${remMonths} bln` : ''}`; bg='#ede9fe'; color='#6b21a8'; emoji='👑'; }
                    return (
                      <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: bg, color }} data-testid="akun-loyalty-badge">
                        <span>{emoji}</span>{label}
                      </span>
                    );
                  })()}
                </div>
              </div>

              {/* PPPoE Username */}
              {customer?.pppoe_username && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-neutral-50 sm:col-span-2" data-testid="akun-pppoe">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${primaryColor}1a`, color: primaryColor }}>
                    <Key className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-neutral-500 uppercase tracking-wide font-medium">PPPoE Username</p>
                    <p className="text-sm font-semibold text-neutral-800 font-mono truncate">{customer.pppoe_username}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Tagihan Tab */}
        {activeTab === 'tagihan' && (
          <>
            {/* Unpaid Invoices - Compact */}
            {unpaidInvoices.length > 0 && (
              <div className="mb-4 sm:mb-5">
                <h2 className="text-sm sm:text-base font-semibold text-slate-800 mb-2 sm:mb-3 flex items-center gap-1.5 dark:text-slate-200">
                  <Clock className="w-4 h-4 text-orange-500" />
                  Tagihan Belum Dibayar ({unpaidInvoices.length})
                </h2>
                <div className="space-y-2">
                  {unpaidInvoices.map((invoice) => (
                    <div key={invoice.id} className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-3.5 sm:p-4 relative overflow-hidden dark:bg-slate-800 dark:border-slate-700">
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-500 to-red-500" aria-hidden />
                      <div className="flex flex-col gap-3 pl-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex flex-col gap-0.5 min-w-0">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <FileText className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                              <h3 className="font-semibold text-xs sm:text-sm text-slate-800 truncate dark:text-slate-200">{invoice.invoice_number}</h3>
                            </div>
                            <p className="text-xs sm:text-sm font-bold text-orange-700 pl-5 dark:text-orange-300" data-testid={`invoice-period-${invoice.id}`}>
                              Tagihan {_formatPeriod(invoice)}
                            </p>
                          </div>
                          <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider flex-shrink-0 dark:bg-orange-900/40 dark:text-orange-300">
                            {invoice.status}
                          </span>
                        </div>
                        <div className="flex-1">
                      <div className="grid grid-cols-3 gap-2 text-[10px] sm:text-xs">
                        <div>
                          <p className="text-gray-500 dark:text-slate-400">Tanggal</p>
                          <p className="font-medium">
                            {new Date(invoice.issue_date || invoice.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short' })}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 dark:text-slate-400">Jatuh Tempo</p>
                          <p className="font-medium text-red-600 dark:text-red-400">
                            {new Date(invoice.due_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short' })}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 dark:text-slate-400">Total</p>
                          <p className="font-bold">Rp {invoice.total?.toLocaleString('id-ID')}</p>
                        </div>
                      </div>
                    </div>
                        <div className="flex gap-2 items-center flex-wrap">
                          <Button
                            onClick={() => handleDownloadInvoice(invoice.id, invoice.invoice_number)}
                            disabled={downloadingInvoice === invoice.id}
                            variant="outline"
                            size="sm"
                            className="h-9 px-3"
                          >
                        {downloadingInvoice === invoice.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </Button>
                      {/* Sync payment status button - for when webhook fails */}
                      {invoice.xendit_invoice_id && (
                        <Button
                          onClick={() => handleSyncPayment(invoice)}
                          disabled={syncingInvoice === invoice.id}
                          variant="outline"
                          size="sm"
                          title="Cek status pembayaran"
                          className="text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
                        >
                          {syncingInvoice === invoice.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                          <Button
                            onClick={() => handlePayment(invoice)}
                            disabled={paymentLoading === invoice.id}
                            size="sm"
                            style={{ background: 'linear-gradient(135deg, #F97316 0%, #EA580C 50%, #DC2626 100%)' }}
                            className="text-white font-bold h-9 px-4 flex-1 sm:flex-none shadow-md shadow-orange-500/30 hover:shadow-lg hover:shadow-orange-500/40 border-0"
                            data-testid={`pay-invoice-${invoice.id}`}
                            title="Bayar tagihan"
                          >
                            {paymentLoading === invoice.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <CreditCard className="w-4 h-4 mr-1.5" />
                                BAYAR
                              </>
                            )}
                          </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Paid Invoices */}
        <div>
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-slate-100">
                Riwayat Pembayaran ({filteredPaidInvoices.length}{filteredPaidInvoices.length !== paidInvoices.length ? ` / ${paidInvoices.length}` : ''})
              </h2>
            </div>
            {paidInvoices.length > 0 && (
              <div className="flex items-center gap-2 text-xs">
                <select
                  value={historyFilterMonth}
                  onChange={(e) => setHistoryFilterMonth(e.target.value)}
                  data-testid="history-filter-month"
                  className="border border-neutral-200 rounded-lg px-2.5 py-1.5 text-xs bg-white text-neutral-700 focus:outline-none focus:ring-2 focus:ring-orange-400 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                  aria-label="Filter bulan"
                >
                  <option value="all">Semua Bulan</option>
                  {_monthNames.map((n, i) => (
                    <option key={i+1} value={i+1}>{n}</option>
                  ))}
                </select>
                <select
                  value={historyFilterYear}
                  onChange={(e) => setHistoryFilterYear(e.target.value)}
                  data-testid="history-filter-year"
                  className="border border-neutral-200 rounded-lg px-2.5 py-1.5 text-xs bg-white text-neutral-700 focus:outline-none focus:ring-2 focus:ring-orange-400 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                  aria-label="Filter tahun"
                >
                  <option value="all">Semua Tahun</option>
                  {historyYears.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                {(historyFilterMonth !== 'all' || historyFilterYear !== 'all') && (
                  <button
                    onClick={() => { setHistoryFilterMonth('all'); setHistoryFilterYear('all'); }}
                    data-testid="history-filter-reset"
                    className="text-orange-600 hover:text-orange-700 font-medium px-1.5 py-1 rounded hover:bg-orange-50 dark:hover:bg-orange-900/20"
                    title="Reset filter"
                  >
                    Reset
                  </button>
                )}
              </div>
            )}
          </div>
          {paidInvoices.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center dark:bg-slate-800">
              <XCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-slate-400">Belum ada riwayat pembayaran</p>
            </div>
          ) : filteredPaidInvoices.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center dark:bg-slate-800">
              <XCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Tidak ada tagihan lunas pada periode yang dipilih
              </p>
              <button
                onClick={() => { setHistoryFilterMonth('all'); setHistoryFilterYear('all'); }}
                className="mt-3 text-xs font-medium text-orange-600 hover:text-orange-700"
              >
                Reset filter
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden dark:bg-slate-800">
              {/* Mobile Card View */}
              <div className="sm:hidden divide-y divide-neutral-100">
                {filteredPaidInvoices.map((invoice) => (
                  <div key={invoice.id} className="p-4 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 dark:text-green-400" />
                        <span className="text-sm font-semibold text-neutral-800 truncate">{invoice.invoice_number}</span>
                      </div>
                      <p className="text-xs font-bold text-green-700 mb-1 dark:text-green-400" data-testid={`invoice-period-paid-${invoice.id}`}>
                        Tagihan {_formatPeriod(invoice)}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-neutral-500">
                        <span title="Tanggal Invoice">
                          <span className="text-neutral-400">Inv:</span> {new Date(invoice.issue_date || invoice.created_at).toLocaleDateString('id-ID')}
                        </span>
                        {invoice.due_date && (
                          <span title="Jatuh Tempo">
                            <span className="text-neutral-400">Tempo:</span> {new Date(invoice.due_date).toLocaleDateString('id-ID')}
                          </span>
                        )}
                        {(invoice.paid_date || invoice.payment_date) && (
                          <span title="Tanggal Bayar" className="text-green-600 font-medium">
                            <span className="text-neutral-400">Bayar:</span> {new Date(invoice.paid_date || invoice.payment_date).toLocaleDateString('id-ID')}
                          </span>
                        )}
                        <span className="font-semibold text-neutral-800 w-full sm:w-auto">Rp {invoice.total?.toLocaleString('id-ID')}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-[10px] font-semibold dark:bg-green-900/40 dark:text-green-300">
                        LUNAS
                      </span>
                      <Button
                        onClick={() => handleDownloadInvoice(invoice.id, invoice.invoice_number)}
                        disabled={downloadingInvoice === invoice.id}
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 hover:bg-orange-50 hover:text-orange-600 dark:hover:bg-orange-900/30"
                        data-testid={`download-invoice-${invoice.id}`}
                      >
                        {downloadingInvoice === invoice.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-50 border-b border-neutral-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wide">No. Invoice</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wide">Periode</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wide">Tgl Invoice</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wide">Tgl Bayar</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wide">Jatuh Tempo</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-600 uppercase tracking-wide">Total</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-600 uppercase tracking-wide">Status</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-600 uppercase tracking-wide">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {filteredPaidInvoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-neutral-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                            <span className="text-sm font-medium text-neutral-800">{invoice.invoice_number}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-slate-800 dark:text-slate-200" data-testid={`invoice-period-cell-${invoice.id}`}>
                          {_formatPeriod(invoice)}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-600">
                          {new Date(invoice.issue_date || invoice.created_at).toLocaleDateString('id-ID')}
                        </td>
                        <td className="px-4 py-3 text-sm text-green-700 font-medium dark:text-green-400">
                          {(invoice.paid_date || invoice.payment_date) ? new Date(invoice.paid_date || invoice.payment_date).toLocaleDateString('id-ID') : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-600">
                          {(invoice.display_due_date || invoice.due_date) ? new Date(invoice.display_due_date || invoice.due_date).toLocaleDateString('id-ID') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-neutral-800">
                          Rp {invoice.total?.toLocaleString('id-ID')}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-xs font-semibold dark:bg-green-900/40 dark:text-green-300">
                            LUNAS
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            onClick={() => handleDownloadInvoice(invoice.id, invoice.invoice_number)}
                            disabled={downloadingInvoice === invoice.id}
                            variant="ghost"
                            size="sm"
                            title="Download & Print Invoice PDF"
                            className="hover:bg-orange-50 hover:text-orange-600 dark:hover:bg-orange-900/30"
                            data-testid={`download-invoice-desktop-${invoice.id}`}
                          >
                            {downloadingInvoice === invoice.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
          </>
        )}

        {/* WiFi Management Tab */}
        {activeTab === 'internet' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 dark:bg-slate-800 dark:border-slate-700">
            {customer?.wan_device_id ? (
              <CustomerWiFiManagement 
                customer={customer} 
                deviceId={customer.wan_device_id} 
              />
            ) : (
              <div className="text-center py-12">
                <Wifi className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700 dark:text-slate-200">Perangkat ONT Tidak Ditemukan</h3>
                <p className="text-slate-500 mt-2 dark:text-slate-400">
                  Akun Anda belum terhubung dengan perangkat ONT/Router.<br/>
                  Silakan hubungi customer service untuk bantuan.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Bantuan Tab */}
        {activeTab === 'help' && (
          <div className="space-y-4" data-testid="help-tab-content">
            {/* Buat Tiket Baru CTA */}
            <button
              onClick={() => setShowTicketModal(true)}
              data-testid="open-ticket-modal"
              className="w-full py-3.5 rounded-xl text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
              style={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                boxShadow: `0 6px 18px ${primaryColor}33`
              }}
            >
              <Plus className="w-4 h-4" /> Buat Tiket Baru
            </button>

            {/* Tickets list */}
            {tickets.length > 0 && (
              <div className="bg-white rounded-xl border border-neutral-200 p-4 dark:bg-slate-800" data-testid="my-tickets-list">
                <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2 dark:text-slate-200">
                  <MessageCircle className="w-4 h-4" style={{ color: primaryColor }} /> Tiket Saya ({tickets.length})
                </h3>
                <div className="space-y-2">
                  {tickets.slice(0, 5).map((t) => (
                    <div key={t.ticket_number} className="flex items-start gap-2 p-2.5 rounded-lg bg-neutral-50 border border-neutral-100">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-mono text-neutral-500">{t.ticket_number}</p>
                        <p className="text-sm font-medium text-slate-800 truncate dark:text-slate-200">{t.title}</p>
                        <p className="text-[11px] text-neutral-400 mt-0.5">{t.created_at?.slice(0, 16).replace('T', ' ')}</p>
                      </div>
                      <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full whitespace-nowrap ${
                        t.status === 'open' ? 'bg-blue-100 text-blue-700' :
                        t.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                        t.status === 'resolved' || t.status === 'closed' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-neutral-100 text-neutral-600'
                      }`}>{t.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Support Accordion */}
            <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden dark:bg-slate-800">
              <button
                onClick={() => setShowContactInfo((v) => !v)}
                data-testid="toggle-contact-info"
                className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-neutral-50 transition-colors"
              >
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">Hubungi Tim Support</span>
                <ChevronDown className={`w-4 h-4 text-neutral-500 transition-transform ${showContactInfo ? 'rotate-180' : ''}`} />
              </button>
              {showContactInfo && (
                <div className="px-4 pb-4 pt-1 border-t border-neutral-100">
                  <div className="flex items-center gap-2 mb-3 mt-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: `${primaryColor}1a`, color: primaryColor }}
                    >
                      <User className="w-4 h-4" />
                    </div>
                    <p className="text-sm font-bold text-slate-800 uppercase tracking-wide dark:text-slate-200">{tenantInfo?.company_name || tenantInfo?.tenant_name || 'Tim Support'}</p>
                  </div>
                  <div className="space-y-1.5">
                    {tenantInfo?.contact_address && (
                      <div className="flex items-start gap-2 p-2.5 rounded-lg bg-neutral-50">
                        <MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: primaryColor }} />
                        <p className="text-xs text-slate-700 leading-relaxed dark:text-slate-200">{tenantInfo.contact_address}</p>
                      </div>
                    )}
                    {tenantInfo?.contact_phone && (
                      <a
                        href={`https://wa.me/${(tenantInfo.contact_phone || '').replace(/[^0-9]/g, '')}`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid="contact-phone-link"
                        className="flex items-center gap-2 p-2.5 rounded-lg bg-neutral-50 hover:bg-neutral-100 transition-colors"
                      >
                        <Phone className="w-4 h-4 shrink-0" style={{ color: primaryColor }} />
                        <span className="text-xs text-slate-700 dark:text-slate-200">{tenantInfo.contact_phone}</span>
                      </a>
                    )}
                    {tenantInfo?.contact_email && (
                      <a
                        href={`mailto:${tenantInfo.contact_email}`}
                        data-testid="contact-email-link"
                        className="flex items-center gap-2 p-2.5 rounded-lg bg-neutral-50 hover:bg-neutral-100 transition-colors"
                      >
                        <Mail className="w-4 h-4 shrink-0" style={{ color: primaryColor }} />
                        <span className="text-xs text-slate-700 break-all dark:text-slate-200">{tenantInfo.contact_email}</span>
                      </a>
                    )}
                    {tenantInfo?.contact_website && (
                      <a
                        href={tenantInfo.contact_website.startsWith('http') ? tenantInfo.contact_website : `https://${tenantInfo.contact_website}`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid="contact-website-link"
                        className="flex items-center gap-2 p-2.5 rounded-lg bg-neutral-50 hover:bg-neutral-100 transition-colors"
                      >
                        <Globe className="w-4 h-4 shrink-0" style={{ color: primaryColor }} />
                        <span className="text-xs text-slate-700 break-all dark:text-slate-200">{tenantInfo.contact_website}</span>
                      </a>
                    )}
                    {!tenantInfo?.contact_address && !tenantInfo?.contact_phone && !tenantInfo?.contact_email && (
                      <p className="text-xs text-neutral-400 text-center py-3">Informasi kontak belum tersedia</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Buat Tiket Modal */}
      <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
        <DialogContent className="sm:max-w-md" data-testid="ticket-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <LifeBuoy className="w-5 h-5" style={{ color: primaryColor }} />
              <span>Buat Tiket Baru</span>
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitTicket} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1 dark:text-slate-200">Judul</label>
              <Input
                type="text"
                value={ticketForm.title}
                onChange={(e) => setTicketForm({ ...ticketForm, title: e.target.value })}
                placeholder="Mis. Internet lambat / putus-putus"
                required
                maxLength={200}
                data-testid="ticket-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1 dark:text-slate-200">Kategori</label>
                <select
                  value={ticketForm.category}
                  onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}
                  className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm bg-white dark:border-slate-700 dark:bg-slate-800"
                  data-testid="ticket-category-select"
                >
                  <option value="general">Umum</option>
                  <option value="connection">Koneksi / Internet</option>
                  <option value="billing">Tagihan</option>
                  <option value="hardware">Perangkat / ONT</option>
                  <option value="other">Lainnya</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1 dark:text-slate-200">Prioritas</label>
                <select
                  value={ticketForm.priority}
                  onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value })}
                  className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm bg-white dark:border-slate-700 dark:bg-slate-800"
                  data-testid="ticket-priority-select"
                >
                  <option value="low">Rendah</option>
                  <option value="medium">Sedang</option>
                  <option value="high">Tinggi</option>
                  <option value="urgent">Mendesak</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1 dark:text-slate-200">Deskripsi</label>
              <Textarea
                value={ticketForm.description}
                onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                placeholder="Jelaskan masalah Anda secara detail..."
                required
                rows={4}
                data-testid="ticket-description-input"
              />
            </div>
            <div className="flex space-x-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowTicketModal(false)}
                className="flex-1"
                disabled={ticketSubmitting}
                data-testid="ticket-cancel-btn"
              >
                Batal
              </Button>
              <Button
                type="submit"
                disabled={ticketSubmitting}
                className="flex-1 text-white border-0"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                  boxShadow: `0 4px 12px ${primaryColor}40`
                }}
                data-testid="ticket-submit-btn"
              >
                {ticketSubmitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Mengirim...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Kirim Tiket</>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Key className="w-5 h-5" style={{ color: primaryColor }} />
              <span>Ubah Password</span>
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">
                Password Saat Ini
              </label>
              <Input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                placeholder="Masukkan password saat ini"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">
                Password Baru
              </label>
              <Input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                placeholder="Masukkan password baru"
                required
                minLength={6}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">
                Konfirmasi Password Baru
              </label>
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                placeholder="Konfirmasi password baru"
                required
                minLength={6}
              />
            </div>
            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowChangePassword(false)}
                className="flex-1"
                disabled={passwordLoading}
              >
                Batal
              </Button>
              <Button
                type="submit"
                className="flex-1 text-white"
                style={{ backgroundColor: primaryColor }}
                disabled={passwordLoading}
              >
                {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ubah Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantCustomerDashboard;
